import React, { useState, useRef, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Checked, Unchecked, Cross, Dragger } from "./Icons";
import { FiUser } from "react-icons/fi";
import {
  getFirestore,
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useProjects } from "../../../context/ProjectsContext";
import { useAuth } from "../../../context/AuthContext";
import AssigneeSelector from "./AssigneeSelector";

const Checklist = ({ todos, taskId, boardId }) => {
  const [todoList, setList] = useState(
    todos.map((todo) => ({ ...todo, assignees: todo.assignees || [] }))
  );
  const [inputValue, setInputValue] = useState("");
  const [selectedTodoId, setSelectedTodoId] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const newTaskRef = useRef(null);
  const db = getFirestore();
  const { activeProject } = useProjects();
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchProjectUsers = async () => {
      if (activeProject) {
        const projectUsersRef = collection(db, "users");
        const q = query(
          projectUsersRef,
          where("uid", "in", activeProject.assignees)
        );
        const querySnapshot = await getDocs(q);
        const users = querySnapshot.docs.map((doc) => doc.data());
        setProjectUsers(users);
      }
    };
    fetchProjectUsers();
  }, [activeProject, db]);

  const addSubTask = async (e) => {
    if (e.key === "Enter" && inputValue.trim() !== "") {
      e.preventDefault();
      const uid = uuidv4();
      const updatedList = [
        ...todoList,
        { id: uid, task: inputValue, done: false, assignees: [] },
      ];
      setList(updatedList);
      await updateTodos(updatedList);
      setInputValue("");
    }
  };

  const checkMark = async (e, todo) => {
    const updatedTodoList = todoList.map((t) =>
      t.task === todo.task ? { ...t, done: !t.done } : t
    );
    setList(updatedTodoList);
    await updateTodos(updatedTodoList);
  };

  const deleteSubTask = async (taskIdToDelete) => {
    const filtered = todoList.filter((t) => t.id !== taskIdToDelete);
    setList(filtered);
    await updateTodos(filtered);
  };

  const endOfDrag = async (result) => {
    const { destination, source } = result;
    if (!destination) return;
    const newOrder = Array.from(todoList);
    const [removed] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, removed);
    setList(newOrder);
    await updateTodos(newOrder);
  };

  const openAssigneeModal = (todoId) => {
    const selectedTodo = todoList.find((todo) => todo.id === todoId);
    setSelectedAssignees(selectedTodo?.assignees || []);
    setSelectedTodoId(todoId);
    setIsModalOpen(true);
  };

  const closeAssigneeModal = () => {
    setIsModalOpen(false);
    setSelectedTodoId(null);
    setSelectedAssignees([]);
  };

  const updateTodoAssignees = async (todoId, assignees) => {
    const updatedTodoList = todoList.map((todo) =>
      todo.id === todoId ? { ...todo, assignees } : todo
    );
    setList(updatedTodoList);
    await updateTodos(updatedTodoList);
  };

  const handleAssigneesSelection = (selectedAssignees) => {
    if (selectedTodoId) {
      updateTodoAssignees(selectedTodoId, selectedAssignees);
    }
    closeAssigneeModal();
  };

  const updateTodos = async (updatedList) => {
    try {
      // Validate that no undefined values are passed to Firestore
      const validatedList = updatedList.map((todo) => ({
        ...todo,
        assignees: todo.assignees.filter(Boolean), // Remove undefined values
        task: todo.task || "", // Ensure task is a string
        done: todo.done || false, // Ensure done is a boolean
        id: todo.id || uuidv4(), // Ensure id is present
      }));

      await updateDoc(
        doc(db, `projects/${activeProject.id}/boards/${boardId}/tasks`, taskId),
        {
          todos: validatedList,
        }
      );
    } catch (error) {
      console.error("Error updating todos:", error);
    }
  };

  return (
    <div>
      <DragDropContext onDragEnd={endOfDrag}>
        <Droppable droppableId={"Checklist"}>
          {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {todoList.map((t, i) => (
                <Draggable key={t.id} draggableId={t.id} index={i}>
                  {(provided, snapshot) => (
                    <div
                      className="flex items-center mt-3 w-full justify-between pr-6"
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <div className="flex w-2/3 items-center">
                        <div className="mr-1" onClick={(e) => checkMark(e, t)}>
                          {t.done ? <Checked /> : <Unchecked />}
                        </div>
                        <h4
                          className={`ml-2 ${
                            t.done ? "line-through text-gray-400" : ""
                          }`}
                        >
                          {t.task}
                        </h4>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center w-[80PX] ">
                          <FiUser onClick={() => openAssigneeModal(t.id)} />
                          {t.assignees.length > 0 && (
                            <div className="ml-3 flex -space-x-1 overflow-hidden max-w-[100px]">
                              {t.assignees.map((assigneeId) => {
                                const user = projectUsers.find(
                                  (user) => user.uid === assigneeId
                                );
                                return user ? (
                                  <img
                                    key={assigneeId}
                                    className="inline-block h-6 w-6 rounded-full ring-2 ring-white"
                                    src={
                                      user.profileImage ||
                                      "https://via.placeholder.com/40"
                                    }
                                    alt={user.displayName}
                                    title={user.displayName}
                                  />
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                        <div
                          className="text-red-400 hover:text-red-700 cursor-pointer"
                          onClick={() => deleteSubTask(t.id)}
                        >
                          <Cross />
                        </div>
                        <div
                          {...provided.dragHandleProps}
                          className="text-gray-600"
                        >
                          <Dragger />
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <input
        ref={newTaskRef}
        type="text"
        name="task"
        placeholder="Add a sub task"
        onKeyPress={addSubTask}
        onChange={(e) => setInputValue(e.target.value)}
        value={inputValue}
        className="border-b border-gray-300 outline-none my-3 w-full"
      />
      <div className="text-sm text-gray-500">
        {inputValue.length}/120 characters
      </div>

      {isModalOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Assign Task</h2>
            <AssigneeSelector
              assignees={projectUsers}
              selectedAssignees={selectedAssignees}
              setSelectedAssignees={setSelectedAssignees}
            />
            <div className="flex justify-end mt-4">
              <button
                onClick={closeAssigneeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md mr-2"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAssigneesSelection(selectedAssignees)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checklist;
